import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faSearch, faPlus, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { crmService } from '../services/crmService';
import { mapSanctionStatus, mapKYCStatus } from '../utils/crmStatusMapper';
import './DeliveryForm.css';

/**
 * Delivery-Specific CRM Partner Search Component
 * 
 * Features:
 * - Debounced search (300ms)
 * - Filters APPROVED profiles only
 * - Displays Legal Entity Name and Trading Short Name
 * - Click outside to close
 * - Matches DeliveryForm styling
 * 
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.fieldName - Field name for the partner name
 * @param {string} props.fieldNameId - Field name for the partner CRM ID
 * @param {string} props.value - Current partner name value
 * @param {Function} props.onChange - Callback with (fieldName, fieldNameId, partnerId, partnerName)
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {boolean} props.required - Whether field is required
 */
const DeliveryPartnerSearch = ({ 
  label,
  fieldName,
  fieldNameId,
  value = '', 
  onChange,
  disabled = false,
  required = false,
  onCreatePartner,
  extraVisibleProfiles = [],
}) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [isPartnerSelected, setIsPartnerSelected] = useState(!!value);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [selectedAddress, setSelectedAddress] = useState('');
  const dropdownRef = useRef(null);

  // Update search term when value changes externally
  useEffect(() => {
    if (value && value !== searchTerm) {
      setSearchTerm(value);
      setIsPartnerSelected(true);
    } else if (!value) {
      setIsPartnerSelected(false);
    }
  }, [value]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch partners when debounced search changes
  useEffect(() => {
    if (debouncedSearch.trim().length >= 2) {
      fetchApprovedPartners(debouncedSearch);
    } else {
      setFilteredPartners([]);
      setSearchFailed(false);
    }
  }, [debouncedSearch]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchApprovedPartners = async (search) => {
    try {
      setLoading(true);
      setSearchFailed(false);
      
      // Use the IsAuthenticated-only quick-search endpoint so trading
      // users without HasCRMAccess can still see existing partners and
      // avoid creating duplicate stubs via the inline create CTA.
      const results = await crmService.quickSearchProfiles(search);
      
      // Filter for APPROVED profiles only.
      // NOTE: CRMProfile.approval_status uses lowercase choices ('approved', 'pending', 'rejected').
      // We accept both casings for robustness, plus undefined for legacy/sparse responses.
      const approvedProfiles = results.filter(profile => {
        const status = (profile.approval_status || '').toString().toLowerCase();
        return status === 'approved' || profile.approval_status === undefined;
      });
      
      const partnersData = approvedProfiles.map(profile => ({
        id: profile.id,
        legal_entity_name: profile.legal_entity_name,
        trading_short_name: profile.trading_short_name,
        head_office_address: profile.head_office_address || '',
        approval_status: profile.approval_status || 'approved',
        display_name: profile.trading_short_name 
          ? `${profile.legal_entity_name} (${profile.trading_short_name})`
          : profile.legal_entity_name
      }));
      
      setFilteredPartners(partnersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching approved partners:', error);
      setFilteredPartners([]);
      setSearchFailed(true);
      setLoading(false);
    }
  };

  // Reset keyboard highlight whenever the visible results change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [filteredPartners, showDropdown]);

  // Same merge-and-render strategy as RecapPartnerSearch:
  // approved hits first, then any session-pending stubs
  // matching the search term, then a "+ Create CRM partner"
  // CTA when ``onCreatePartner`` was passed by the parent.
  const mergedPartners = (() => {
    const term = debouncedSearch.trim().toLowerCase();
    const approvedIds = new Set(filteredPartners.map(p => p.id));
    const matchingExtras = (extraVisibleProfiles || []).filter(p => {
      if (!p || approvedIds.has(p.id)) return false;
      const hay = (
        (p.legal_entity_name || '') + ' ' + (p.trading_short_name || '')
      ).toLowerCase();
      return hay.includes(term);
    }).map(p => ({
      id: p.id,
      legal_entity_name: p.legal_entity_name,
      trading_short_name: p.trading_short_name,
      head_office_address: p.head_office_address || '',
      approval_status: p.approval_status || 'pending',
      display_name: p.trading_short_name
        ? `${p.legal_entity_name} (${p.trading_short_name})`
        : p.legal_entity_name,
    }));
    return [...filteredPartners, ...matchingExtras];
  })();

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      return;
    }
    if (!showDropdown || searchFailed || mergedPartners.length === 0) return;
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, mergedPartners.length - 1));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelectPartner(mergedPartners[highlightIndex]);
    }
  };

  const handleSelectPartner = async (partner) => {
    setSearchTerm(partner.display_name);
    setShowDropdown(false);
    setIsPartnerSelected(true);
    setSelectedAddress(partner.head_office_address || '');

    try {
      // Fetch full CRM profile to get sanction and KYC status
      const fullProfile = await crmService.getProfile(partner.id);
      
      // Map sanction status
      const sanctionStatus = mapSanctionStatus(fullProfile);
      
      // Map KYC status
      const kycStatus = mapKYCStatus(fullProfile);
      
      // Pass all data to parent, including status information
      onChange(fieldName, fieldNameId, partner.id, partner.legal_entity_name, {
        sanctionStatus,
        kycStatus
      });
    } catch (error) {
      console.error('Error fetching full CRM profile:', error);
      // Still pass the basic data even if status fetch fails
      onChange(fieldName, fieldNameId, partner.id, partner.legal_entity_name, {
        sanctionStatus: 'pending',
        kycStatus: 'pending'
      });
    }
  };

  return (
    <div className="form-group" ref={dropdownRef} style={{ width: '100%', position: 'relative', zIndex: showDropdown ? 1001 : 1 }}>
      {label && (
        <label htmlFor={fieldName}>
          {label} {required && <span className="required-field">*</span>}
        </label>
      )}
      <div style={{ position: 'relative', width: '100%' }}>
        <div style={{ position: 'relative', width: '100%' }}>
          {!isPartnerSelected && (
            <FontAwesomeIcon 
              icon={faSearch} 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#666',
                pointerEvents: 'none',
                zIndex: 1
              }} 
            />
          )}
          <input
            type="text"
            id={fieldName}
            name={fieldName}
            className="form-input"
            style={{ 
              paddingLeft: isPartnerSelected ? '12px' : '36px', 
              width: '100%',
              fontWeight: isPartnerSelected ? '700' : '400'
            }}
            value={searchTerm}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchTerm(newValue);
              setShowDropdown(true);
              setIsPartnerSelected(false);
              setSelectedAddress('');

              // If input is cleared, clear the stored field values
              if (newValue === '') {
                onChange(fieldName, fieldNameId, null, '', {
                  sanctionStatus: 'pending',
                  kycStatus: 'pending'
                });
              }
            }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search APPROVED CRM profiles by Legal Entity Name..."
            disabled={disabled}
          />
        </div>
        {isPartnerSelected && selectedAddress && (
          <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <FontAwesomeIcon icon={faMapMarkerAlt} /> {selectedAddress}
          </div>
        )}

        {showDropdown && debouncedSearch.trim().length >= 2 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            maxHeight: '280px',
            overflowY: 'auto',
            zIndex: 10000,
            marginTop: '4px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            {(() => {
              const merged = mergedPartners;

              if (loading) {
                return (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                    Searching...
                  </div>
                );
              }
              if (searchFailed) {
                // Row 127: a failed search is not "no partners found" -- and
                // never offers to create one.
                return (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                    Search failed —{' '}
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        fetchApprovedPartners(debouncedSearch);
                      }}
                      style={{ background: 'none', border: 'none', padding: 0, color: '#4a6b25', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                    >
                      try again
                    </button>
                  </div>
                );
              }
              return (
                <>
                  {merged.length === 0 ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                      No APPROVED partners found for "{debouncedSearch}"
                    </div>
                  ) : (
                    merged.map((partner, idx) => {
                      const isPending = (partner.approval_status || '').toLowerCase() === 'pending';
                      return (
                        <div
                          key={partner.id}
                          ref={idx === highlightIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
                          onClick={() => handleSelectPartner(partner)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s',
                            backgroundColor: idx === highlightIndex ? '#f5f5f5' : 'white'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx === highlightIndex ? '#f5f5f5' : 'white'}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '600', color: '#4a6b25' }}>
                              {partner.legal_entity_name}
                            </span>
                            {isPending && (
                              <span
                                title="Created from this draft. Awaiting CRM approval."
                                style={{
                                  fontSize: '0.7em',
                                  fontWeight: 600,
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  border: '1px solid #f59e0b',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Pending Approval
                              </span>
                            )}
                          </div>
                          {partner.trading_short_name && (
                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                              Trading as: {partner.trading_short_name}
                            </div>
                          )}
                          {partner.head_office_address && (
                            <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <FontAwesomeIcon icon={faMapMarkerAlt} /> {partner.head_office_address}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}

                  {onCreatePartner && (
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDropdown(false);
                        onCreatePartner(debouncedSearch);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        border: 'none',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#f8fafc',
                        color: '#4a6b25',
                        fontWeight: 600,
                        fontSize: '0.9em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Create CRM partner "{debouncedSearch}"
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
      {debouncedSearch.trim().length > 0 && debouncedSearch.trim().length < 2 && (
        <span style={{ fontSize: '0.85em', color: '#666', marginTop: '2px', display: 'block' }}>
          Type at least 2 characters to search APPROVED CRM profiles
        </span>
      )}
    </div>
  );
};

export default DeliveryPartnerSearch;

